/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Device } from '@studio/models/device';
import {
  DeviceRegistryInterfaceSection,
  StatusDeviceConnectionStatus,
  type DeviceRegistryCommand
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  DeviceRegistryInterfaceSectionType,
  type DeviceRegistryCommandKey,
  type DeviceRegistryInterfaceItem
} from '@tryiris-ai/common.protojs/api/v1/device_settings';

import type { DeviceSetting as PBDeviceSetting } from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';

import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js';
import { calculateConditions } from './conditions.js';
import type { InterfaceCondition } from './conditions.js';
import { calculateDefaults } from './defaults.js';
import { useDeviceSettings, handleRestorePreset } from './helpers.jsx';
import { locateProxy } from '#~/agent/index.js';
import type { Proxy } from '#~/agent/index.js';
import type { DeviceSetting } from '#~/device/settings/index.js';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';

/** @internal */
export const handleRestoreDefaults = (
  agent: T_Maybe<Proxy>,
  deviceId: Device['id'],
  nonDefaultMap: Record<number, DeviceSetting>,
  defaultsMap: Record<number, number>
): void => {
  const nextSettings: Record<string, DeviceSetting> = {};

  Object.entries(nonDefaultMap).forEach(([key, value]) => {
    const def = defaultsMap[Number(key)];

    if (def === undefined) {
      return;
    }

    const nextSetting: DeviceSetting = {};

    if (value.toggle !== undefined) {
      nextSetting.toggle = Boolean(def);
    }

    if (value.direct !== undefined) {
      nextSetting.direct = def;
    }

    if (value.select !== undefined) {
      nextSetting.select = def;
    }

    nextSettings[Number(key)] = nextSetting;
  });

  handleRestorePreset(agent, deviceId, nextSettings).catch(noop);
};

const isDefault = (
  key: DeviceRegistryCommandKey,
  value: DeviceSetting,
  commandMap: Record<number, DeviceRegistryCommand>,
  conditionsMap: Record<number, InterfaceCondition>,
  defaultsMap: Record<number, number>
): boolean => {
  const cmd = commandMap[Number(key)];

  if (!cmd) {
    return true;
  }

  const conditions = conditionsMap[Number(key)];

  if (conditions === undefined) {
    return true;
  }

  if (conditions.isHidden || conditions.isDisabled) {
    return true;
  }

  const def = defaultsMap[Number(key)];

  if (def === undefined) {
    return true;
  }

  if (value.toggle !== undefined) {
    return value.toggle === Boolean(def);
  }

  if (value.direct !== undefined) {
    return value.direct === def;
  }

  if (value.select !== undefined) {
    return value.select === def;
  }

  return false;
};

const calculateNonDefaultMap = (
  deviceSettings: Record<number, DeviceSetting>,
  commandMap: Record<number, DeviceRegistryCommand>,
  conditionsMap: Record<number, InterfaceCondition>,
  defaultsMap: Record<number, number>
): Record<number, DeviceSetting> =>
  Object.entries(deviceSettings).reduce<Record<string, DeviceSetting>>(
    (accumulator, [key, value]) => {
      if (!isDefault(Number(key), value, commandMap, conditionsMap, defaultsMap)) {
        accumulator[key] = value;
      }

      return accumulator;
    },
    {}
  );

/** @internal */
export const sortWeightedItems = (a: [string, any], b: [string, any]): number => {
  const weightA = a[1].sortWeight ?? 0;
  const weightB = b[1].sortWeight ?? 0;

  // Sort by weight in ascending order
  if (weightA !== weightB) {
    return weightA - weightB;
  }

  // Fallback to alphabetical order by label when weights are equal
  const labelA = a[1].label ?? '';
  const labelB = b[1].label ?? '';
  return labelA.localeCompare(labelB);
};

const calculateInterfaceMap = (
  interfaceSections: ReadonlyObjectDeep<Record<string, DeviceRegistryInterfaceSection>> | undefined
): Record<number, DeviceRegistryInterfaceItem> => {
  const interfaceMap: Record<number, DeviceRegistryInterfaceItem> = {};

  Object.entries(interfaceSections ?? {}).forEach(([_, section]) => {
    Object.entries(section.items).forEach(([_, item]) => {
      interfaceMap[item.key] = <DeviceRegistryInterfaceItem>(<unknown>item);
    });
  });

  return interfaceMap;
};

const calculateCommandMap = (
  commandDefinitions: ReadonlyObjectDeep<Record<string, DeviceRegistryCommand>> | undefined
): Record<number, DeviceRegistryCommand> => {
  const commandMap: Record<number, DeviceRegistryCommand> = {};

  Object.entries(commandDefinitions ?? {}).forEach(([key, cmd]) => {
    commandMap[Number(key)] = <DeviceRegistryCommand>(<unknown>cmd);
  });

  return commandMap;
};

/**
 * Filter interface sections by a specific section type
 *
 * @param interfaceSections - The interface sections to filter
 * @param sectionType - The section type to filter by
 * @returns A filtered set of interface section keys
 * @internal
 */
export const filterInterfaceSectionsByType = (
  interfaceSections: ReadonlyObjectDeep<Record<string, DeviceRegistryInterfaceSection>> | undefined,
  sectionType: DeviceRegistryInterfaceSectionType
): Set<number> => {
  return new Set(
    Object.values(interfaceSections ?? {})
      .filter((section) => section.type === sectionType)
      .flatMap((section) => Object.values(section.items).map((item) => item.key))
  );
};

/**
 * Merges streaming sections into the RTSP section by combining their items.
 * This consolidates all streaming-related settings under one RTSP section.
 *
 * @param interfaceSections - The original interface sections
 * @returns Modified interface sections with streaming sections merged into RTSP
 */
const mergeStreamingSectionsIntoRTSP = (
  interfaceSections: ReadonlyObjectDeep<Record<string, DeviceRegistryInterfaceSection>> | undefined
): Record<string, DeviceRegistryInterfaceSection> => {
  if (!interfaceSections) {
    return {};
  }

  // Create a mutable copy of the interface sections
  const modifiedSections: Record<string, DeviceRegistryInterfaceSection> = {};

  // Streaming section types that should be merged into RTSP
  const streamingSectionTypes = new Set([
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING,
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_1,
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_2,
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_3,
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_4,
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_5,
    DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_STREAMING_STREAM_6
  ]);

  let rtspSection: any = null;
  let rtspSectionId: string | null = null;
  const streamingSections: any[] = [];

  // First pass: identify RTSP section and streaming sections
  Object.entries(interfaceSections).forEach(([id, section]) => {
    if (section.type === DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_RTSP) {
      rtspSection = section;
      rtspSectionId = id;
    } else if (streamingSectionTypes.has(section.type)) {
      streamingSections.push(section);
    } else {
      // Keep non-streaming sections as-is
      modifiedSections[id] = { ...section } as DeviceRegistryInterfaceSection;
    }
  });

  // If we have an RTSP section, merge streaming sections into it
  if (rtspSection && rtspSectionId) {
    // Create a new items object combining RTSP items with streaming items
    const mergedItems = { ...rtspSection.items };

    // Add all items from streaming sections to the RTSP section
    streamingSections.forEach((streamingSection) => {
      Object.entries(streamingSection.items || {}).forEach(([itemId, item]) => {
        mergedItems[itemId] = item;
      });
    });

    // Create the merged RTSP section
    const mergedRtspSection = {
      ...rtspSection,
      items: mergedItems
    } as DeviceRegistryInterfaceSection;

    modifiedSections[rtspSectionId] = mergedRtspSection;
  } else {
    // If no RTSP section exists, keep streaming sections as-is
    Object.entries(interfaceSections).forEach(([id, section]) => {
      if (streamingSectionTypes.has(section.type)) {
        modifiedSections[id] = { ...section } as DeviceRegistryInterfaceSection;
      }
    });
  }

  return modifiedSections;
};

interface Wiring {
  agent: T_Maybe<Proxy>;
  canRestoreDefaults: boolean;
  isOffline: boolean;
  commandMap: Record<number, DeviceRegistryCommand>;
  conditionsMap: Record<number, InterfaceCondition>;
  defaultsMap: Record<number, number>;
  deviceSettings: Record<number, DeviceSetting>;
  interfaceSections: ReadonlyObjectDeep<Record<string, DeviceRegistryInterfaceSection>> | undefined;
  nonDefaultMap: Record<number, DeviceSetting>;
  nonDefaultImageSettingsMap: Record<number, DeviceSetting>;
}

/** @internal */
export const useWiring = (
  deviceId: Device['id'],
  overrides?: Record<string, DeepPartial<PBDeviceSetting>>
): Wiring => {
  const agent = locateProxy(deviceId);

  // Registry metadata can arrive without changing any setting values. The
  // depot intentionally preserves equal devices, so subscribe to the owning
  // proxy's metadata directly instead of relying on a values-driven render.
  const metadata = useMultiDeviceStateQuery({
    transform: ({ id }) => {
      if (id !== deviceId) {
        return undefined;
      }

      // Ownership can change between renders when a proxy is replaced.
      const settings = locateProxy(deviceId)?.state.status?.device[deviceId]?.deviceSettings;
      return {
        commandDefinitions: settings?.commandDefinitions,
        interfaceSections: settings?.interfaceSections
      };
    },
    // Central dispatch already coalesces transactions; do not discard a lone
    // metadata update arriving inside the default per-subscriber interval.
    minInterval: 0
  })[deviceId];
  const { commandDefinitions, interfaceSections: originalInterfaceSections } = metadata ?? {};

  // Apply section merging to group streaming sections under RTSP
  const interfaceSections = mergeStreamingSectionsIntoRTSP(originalInterfaceSections);

  const videoStatus = agent?.state.status?.device[deviceId]?.video?.connectionStatus?.status;
  const controlStatus = agent?.state.status?.device[deviceId]?.movement?.connectionStatus?.status;
  const isOffline =
    videoStatus !== StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED &&
    controlStatus !== StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED;

  const commandMap = calculateCommandMap(commandDefinitions);
  const deviceSettings = useDeviceSettings(deviceId, overrides);
  const interfaceMap = calculateInterfaceMap(interfaceSections);

  const defaultsMap = calculateDefaults(commandMap);
  const conditionsMap = calculateConditions(interfaceMap, commandMap, deviceSettings);

  const nonDefaultMap = calculateNonDefaultMap(
    deviceSettings,
    commandMap,
    conditionsMap,
    defaultsMap
  );

  const imageSettingsKeys = filterInterfaceSectionsByType(
    originalInterfaceSections,
    DeviceRegistryInterfaceSectionType.IMAGE_SETTINGS_TRAY
  );

  const nonDefaultImageSettingsMap = Object.fromEntries(
    Object.entries(nonDefaultMap).filter(([key]) => imageSettingsKeys.has(Number(key)))
  );

  const canRestoreDefaults = Object.keys(nonDefaultImageSettingsMap).length !== 0;

  return {
    agent,
    canRestoreDefaults,
    isOffline,
    commandMap,
    conditionsMap,
    defaultsMap,
    deviceSettings,
    interfaceSections,
    nonDefaultMap,
    nonDefaultImageSettingsMap
  };
};
